import { useState, useRef, useCallback } from 'react';
import { X, Sparkles, Loader2, Search } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useToast } from '@/hooks/useToast';
import { useMacSuggest } from '@/hooks/useMacSuggest';
import { fetchAdminReplyPlaybooks, type AdminReplyPlaybook } from '@/api/adminReplyPlaybooks';
import { CopilotVariantsList } from './CopilotVariantsList';
import { ALL_MODES, MODE_LABELS, type MacSuggestion, type CopilotMode } from './types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  phone: string;
  conversationId?: string;
  patientId?: string;
  tenantId: string;
  onApplyVariant: (text: string, suggestionId: string, eventId: string, mode: string, variantIdx: number) => void;
}

export function CopilotDrawer({
  isOpen,
  onClose,
  phone,
  conversationId,
  patientId,
  tenantId,
  onApplyVariant,
}: Props) {
  const toast = useToast();
  const macSuggest = useMacSuggest(tenantId);

  const [intent, setIntent] = useState('');
  const [mode, setMode] = useState<CopilotMode | ''>('');
  const [useFullContext, setUseFullContext] = useState(false);
  const [variants, setVariants] = useState(1);
  const [suggestions, setSuggestions] = useState<MacSuggestion[]>([]);
  const [playbooks, setPlaybooks] = useState<AdminReplyPlaybook[]>([]);
  const [playbooksLoading, setPlaybooksLoading] = useState(false);
  const [currentEventId, setCurrentEventId] = useState('');
  const [regeneratingIdx, setRegeneratingIdx] = useState<number | null>(null);

  const intentRef = useRef<HTMLTextAreaElement>(null);

  const handleGenerate = useCallback(async () => {
    if (!intent.trim()) return;
    setSuggestions([]);
    try {
      const { data, eventId } = await macSuggest.mutateAsync({
        phone,
        conversation_id: conversationId,
        patient_id: patientId,
        intent: intent.trim(),
        mode: mode || undefined,
        variants,
        use_full_context: useFullContext,
      });

      if (data.status === 'ok' && data.suggestions?.length) {
        setSuggestions(data.suggestions);
        setCurrentEventId(eventId ?? data.event_id ?? '');
      } else {
        toast.showError(data.message || 'Не удалось сгенерировать ответ');
      }
    } catch {
      toast.showError('Не удалось сгенерировать ответ');
    }
  }, [intent, mode, variants, useFullContext, phone, conversationId, patientId, macSuggest, toast]);

  const handleRegenerate = useCallback(async (idx: number) => {
    if (!intent.trim()) return;
    setRegeneratingIdx(idx);
    try {
      const { data, eventId } = await macSuggest.mutateAsync({
        phone,
        conversation_id: conversationId,
        patient_id: patientId,
        intent: intent.trim(),
        mode: mode || undefined,
        variants: 1,
        use_full_context: useFullContext,
      });

      if (data.status === 'ok' && data.suggestions?.length) {
        setSuggestions(prev => {
          const next = [...prev];
          next[idx] = data.suggestions![0];
          return next;
        });
        setCurrentEventId(eventId ?? data.event_id ?? currentEventId);
      } else {
        toast.showError(data.message || 'Не удалось регенерировать вариант');
      }
    } catch {
      toast.showError('Не удалось регенерировать вариант');
    } finally {
      setRegeneratingIdx(null);
    }
  }, [intent, mode, useFullContext, phone, conversationId, patientId, macSuggest, toast, currentEventId]);

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast.showSuccess('Скопировано'),
      () => toast.showError('Не удалось скопировать'),
    );
  }, [toast]);

  const handleFindPlaybooks = useCallback(async () => {
    setPlaybooksLoading(true);
    try {
      const result = await fetchAdminReplyPlaybooks();
      const needle = intent.trim().toLowerCase();
      const matched = needle
        ? result.filter((item) => [
          item.title,
          item.payload.topic,
          item.payload.recommended_answer,
          ...(item.payload.trigger_phrases || []),
        ].some((value) => value.toLowerCase().includes(needle)))
        : result;
      setPlaybooks(matched.slice(0, 5));
    } catch {
      toast.showError('Не удалось найти подсказки');
    } finally {
      setPlaybooksLoading(false);
    }
  }, [intent, toast]);

  const handleUse = useCallback((suggestion: MacSuggestion, idx: number) => {
    onApplyVariant(suggestion.text, suggestion.id, currentEventId, suggestion.mode ?? mode, idx);
    onClose();
  }, [onApplyVariant, onClose, currentEventId, mode]);

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]"
        onClick={handleClose}
      />

      <div className={cn(
        'fixed right-0 top-0 bottom-0 z-50 w-full max-w-[480px]',
        'bg-shell-bg border-l border-shell-border shadow-2xl',
        'flex flex-col overflow-hidden',
        'animate-in slide-in-from-right-8 duration-200'
      )}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-shell-border bg-shell-surface flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-violet-100">
              <Sparkles className="w-4 h-4 text-violet-600" />
            </div>
            <span className="font-semibold text-shell-strong">AI Copilot</span>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-shell-hover text-shell-muted hover:text-shell-strong transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Intent */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-shell-strong">
              Суть ответа <span className="text-red-500">*</span>
            </label>
            <textarea
              ref={intentRef}
              value={intent}
              onChange={e => setIntent(e.target.value.slice(0, 2000))}
              placeholder="О чём нужно ответить пациенту..."
              rows={3}
              className="shell-input w-full resize-none text-sm"
            />
            <div className="text-right text-[10px] text-shell-muted">{intent.length}/2000</div>
          </div>

          <div className="rounded-xl border border-shell-border bg-shell-card p-3">
            <button
              type="button"
              onClick={handleFindPlaybooks}
              disabled={playbooksLoading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-shell-elevated px-3 py-2 text-xs font-semibold text-shell-strong hover:bg-shell-hover disabled:opacity-50"
            >
              {playbooksLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
              Найти готовые подсказки
            </button>
            {playbooks.length > 0 && (
              <div className="mt-3 space-y-2">
                {playbooks.map((playbook) => (
                  <button
                    key={`${playbook.tenant_id}-${playbook.id}`}
                    type="button"
                    onClick={() => setIntent(playbook.payload.recommended_answer.slice(0, 2000))}
                    className="w-full rounded-lg border border-shell-border bg-shell-elevated px-3 py-2 text-left hover:bg-shell-hover"
                  >
                    <div className="text-xs font-semibold text-shell-strong">{playbook.title}</div>
                    <div className="mt-1 line-clamp-2 text-xs text-shell-muted">{playbook.payload.recommended_answer}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mode */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-shell-strong">Режим</label>
            <div className="grid grid-cols-1 gap-1.5">
              {ALL_MODES.map(m => (
                <label key={m} className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="radio"
                    name="copilot-mode"
                    value={m}
                    checked={mode === m}
                    onChange={() => setMode(m)}
                    className="accent-violet-600"
                  />
                  <span className={cn(
                    'text-sm transition-colors',
                    mode === m ? 'text-violet-700 font-medium' : 'text-shell-muted group-hover:text-shell-strong'
                  )}>
                    {MODE_LABELS[m]}
                  </span>
                </label>
              ))}
              {mode && (
                <button
                  onClick={() => setMode('')}
                  className="text-xs text-shell-muted hover:text-shell-strong text-left mt-0.5 underline-offset-2 hover:underline"
                >
                  Сбросить режим
                </button>
              )}
            </div>
          </div>

          {/* CCS */}
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={useFullContext}
              onChange={e => setUseFullContext(e.target.checked)}
              className="mt-0.5 accent-violet-600"
            />
            <span className="text-sm text-shell-muted leading-snug">
              Использовать полный контекст пациента (CCS)
            </span>
          </label>

          {/* Variants slider */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-shell-strong">
              Вариантов: <span className="text-violet-600">{variants}</span>
            </label>
            <input
              type="range"
              min={1}
              max={3}
              step={1}
              value={variants}
              onChange={e => setVariants(Number(e.target.value))}
              className="w-full accent-violet-600"
            />
            <div className="flex justify-between text-[10px] text-shell-muted px-0.5">
              <span>1</span>
              <span>2</span>
              <span>3</span>
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={!intent.trim() || macSuggest.isPending}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition-all',
              'bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.99]',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-violet-600'
            )}
          >
            {macSuggest.isPending
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Генерирую...</>
              : <><Sparkles className="w-4 h-4" /> Сгенерировать</>
            }
          </button>

          {/* Variants list */}
          <CopilotVariantsList
            suggestions={suggestions}
            onUse={handleUse}
            onRegenerate={handleRegenerate}
            onCopy={handleCopy}
            regeneratingIdx={regeneratingIdx}
          />
        </div>
      </div>
    </>
  );
}
